import { useEffect, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ClipboardList, Loader2 } from 'lucide-react';
import { api } from '@/lib/api';
import { kontrolaHesla, HESLO_HINT } from '@/lib/passwordPolicy';
import type {
  ResetPasswordInput,
  ResetPasswordResponse,
  VerifyResetTokenInput,
  VerifyResetTokenResponse,
} from '@/types/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';

type TokenState =
  | { kind: 'checking' }
  | { kind: 'missing' }
  | { kind: 'invalid' }
  | { kind: 'valid' }
  | { kind: 'success' };

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const { toast } = useToast();

  const [tokenState, setTokenState] = useState<TokenState>(() =>
    token ? { kind: 'checking' } : { kind: 'missing' },
  );
  const [novehHeslo, setNoveHeslo] = useState('');
  const [confirmHeslo, setConfirmHeslo] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Policy validace zrcadli backend (lib/passwordPolicy.ts). E-mail tu nemame
  // (heslo se nastavuje z reset-linku), volame tedy bez nej.
  const hesloDuvody = novehHeslo.length > 0 ? kontrolaHesla(novehHeslo) : [];
  const hesloMismatch = confirmHeslo.length > 0 && novehHeslo !== confirmHeslo;
  const canSubmit =
    !submitting &&
    novehHeslo.length > 0 &&
    confirmHeslo.length > 0 &&
    hesloDuvody.length === 0 &&
    novehHeslo === confirmHeslo;

  // Pri mountu overime token jednou — kdyby byl uzivatel zaplaven kopii URL
  // bez tokenu, sloziti by se to zaseklo na 4xx.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const run = async () => {
      try {
        const body: VerifyResetTokenInput = { token };
        const res = await api<VerifyResetTokenResponse>('/auth/verify-reset-token', {
          method: 'POST',
          body: JSON.stringify(body),
        });
        if (cancelled) return;
        setTokenState({ kind: res.valid ? 'valid' : 'invalid' });
      } catch {
        if (cancelled) return;
        setTokenState({ kind: 'invalid' });
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (novehHeslo !== confirmHeslo) {
      toast({
        title: 'Hesla se neshodují',
        description: 'Zadejte stejné heslo do obou polí.',
        variant: 'destructive',
      });
      return;
    }
    setSubmitting(true);
    try {
      const body: ResetPasswordInput = { token, noveHeslo: novehHeslo };
      const res = await api<ResetPasswordResponse>('/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      if (res.ok) {
        setTokenState({ kind: 'success' });
      } else {
        toast({ title: 'Obnova hesla selhala', variant: 'destructive' });
      }
    } catch (err) {
      toast({
        title: 'Obnova hesla selhala',
        description: (err as Error).message,
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="items-center text-center">
          <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <ClipboardList className="h-7 w-7" />
          </div>
          <CardTitle>Nastavení nového hesla</CardTitle>
        </CardHeader>

        <CardContent>
          {tokenState.kind === 'checking' && (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}

          {(tokenState.kind === 'missing' || tokenState.kind === 'invalid') && (
            <div className="space-y-4">
              <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-3 text-sm text-destructive">
                Odkaz je neplatný nebo expirovaný.
              </div>
              <Link
                to="/forgot-password"
                className="block text-center text-sm text-muted-foreground hover:underline"
              >
                Vyžádat nový odkaz
              </Link>
            </div>
          )}

          {tokenState.kind === 'valid' && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="nove-heslo">Nové heslo</Label>
                <Input
                  id="nove-heslo"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={6}
                  value={novehHeslo}
                  onChange={(e) => setNoveHeslo(e.target.value)}
                  disabled={submitting}
                  aria-invalid={hesloDuvody.length > 0}
                />
                {hesloDuvody.length > 0 ? (
                  <ul className="space-y-0.5 text-xs text-destructive">
                    {hesloDuvody.map((d) => (
                      <li key={d}>{d}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-muted-foreground">{HESLO_HINT}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirm-heslo">Potvrzení hesla</Label>
                <Input
                  id="confirm-heslo"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={6}
                  value={confirmHeslo}
                  onChange={(e) => setConfirmHeslo(e.target.value)}
                  disabled={submitting}
                  aria-invalid={hesloMismatch}
                />
                {hesloMismatch && (
                  <p className="text-xs text-destructive">Hesla se neshodují.</p>
                )}
              </div>

              <Button type="submit" className="w-full" disabled={!canSubmit}>
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Nastavit nové heslo'}
              </Button>
            </form>
          )}

          {tokenState.kind === 'success' && (
            <div className="space-y-4">
              <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-3 text-sm text-emerald-900">
                Heslo bylo úspěšně změněno. Nyní se můžete přihlásit.
              </div>
              <Link
                to="/login"
                className="block text-center text-sm text-muted-foreground hover:underline"
              >
                Přejít na přihlášení
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
